'use client';

import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

import { companies } from '@/constants';
import { themes, useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';

// Duplicate the array to create a seamless loop
const duplicatedCompanies = [...companies, ...companies];

export default function PortfolioSection() {
  const { currentTheme } = useTheme();
  const isMobile = useIsMobile();

  return (
    <div
      id="portfolio"
      className="relative min-h-[50vh] w-full overflow-hidden py-24"
    >
      <div className="relative z-[2] mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-4 text-3xl font-bold sm:text-5xl"
          >
            Our Portfolio Companies
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-base opacity-80 sm:text-lg"
          >
            Proudly backing 300+ innovative companies that are shaping the
            future of technology and business.
          </motion.p>
        </div>

        {/* Marquee Container */}
        <div className="relative mt-16 overflow-hidden">
          {/* Gradient Overlays */}
          <div className="pointer-events-none absolute left-0 top-0 z-[3] h-full w-32 bg-gradient-to-r from-[var(--bg-start)] to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 z-[3] h-full w-32 bg-gradient-to-l from-[var(--bg-start)] to-transparent" />

          {/* Marquee Content */}
          <div className="flex gap-8">
            <motion.div
              className="flex shrink-0 gap-8"
              animate={{ x: ['0%', '-50%'] }}
              transition={{
                duration: isMobile ? 60 : 45, // Slower on mobile
                ease: 'linear',
                repeat: Infinity,
              }}
              style={{
                willChange: 'transform',
              }}
            >
              {duplicatedCompanies.map((company, index) => (
                <div
                  key={`${index >= companies.length ? 'dup-' : ''}${company.name}`}
                  className="flex h-24 w-40 items-center justify-center rounded-2xl bg-white p-4 backdrop-blur-lg md:h-32 md:w-48"
                >
                  <div className="relative size-full">
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="absolute inset-0 size-full object-contain"
                      loading="eager"
                    />
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 text-center"
        >
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-all hover:scale-105 sm:text-base"
            style={{
              background: themes[currentTheme].gradient,
              willChange: 'transform',
            }}
          >
            Explore Companies
            <MagnifyingGlassIcon className="size-4 sm:size-5" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
